import clsx from 'clsx'
import React, {useMemo, useState} from 'react'
import {KTSVG} from '../../../../_gori/helpers'
import {useIntl} from 'react-intl'
import {InputCheckNoFormik, InputTextNoFormik} from '../../../../_gori/partials/widgets'

export type BranchesMenuAccount = {
  id: string | number
  name: string
  checked?: boolean
}

export type BranchesMenuBranch = {
  id: string | number
  name: string
  checked?: boolean
  expanded?: boolean
  accounts: BranchesMenuAccount[]
}

export const DEFAULT_BRANCHES: BranchesMenuBranch[] = []

type AccountBranchesResponse = {
  total_branches: number
  branches: Array<{
    id: string | number
    name: string
    accounts: Array<{
      id: string | number
      name: string
    }>
  }>
}

export const mapBranchesResponseToMenuBranches = (
  response?: AccountBranchesResponse
): BranchesMenuBranch[] => {
  if (!response?.branches?.length) {
    return []
  }

  return response.branches.map((branch) => ({
    id: branch.id,
    name: branch.name,
    checked: true,
    expanded: false,
    accounts: branch.accounts.map((account) => ({
      id: account.id,
      name: account.name,
      checked: true,
    })),
  }))
}

export const getSelectedAccountIds = (branches: BranchesMenuBranch[]) =>
  branches.flatMap((branch) =>
    branch.accounts.filter((account) => account.checked).map((account) => account.id)
  )

type Props = {
  branches?: BranchesMenuBranch[]
  onBranchesChange?: (branches: BranchesMenuBranch[]) => void
  showTitle?: boolean
  className?: string
}

const BranchesMenu: React.FC<Props> = ({
  branches: controlledBranches,
  onBranchesChange,
  showTitle = true,
  className,
}) => {
  const [search, setSearch] = useState('')
  const [internalBranches, setInternalBranches] = useState<BranchesMenuBranch[]>(DEFAULT_BRANCHES)
  const intl = useIntl()

  const branches = controlledBranches ?? internalBranches

  const setBranchesAndNotify = (nextBranches: BranchesMenuBranch[]) => {
    if (onBranchesChange) {
      onBranchesChange(nextBranches)
      return
    }
    setInternalBranches(nextBranches)
  }

  const filteredBranches = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return branches

    return branches
      .map((branch) => {
        const branchMatched = branch.name.toLowerCase().includes(keyword)
        const matchedAccounts = branch.accounts.filter((account) =>
          account.name.toLowerCase().includes(keyword)
        )

        if (!branchMatched && matchedAccounts.length === 0) {
          return null
        }

        return {
          ...branch,
          expanded: branch.expanded || branchMatched || matchedAccounts.length > 0,
          accounts: branchMatched ? branch.accounts : matchedAccounts,
        }
      })
      .filter(Boolean) as BranchesMenuBranch[]
  }, [branches, search])

  const totalBranches = branches.length
  const allSelected = useMemo(
    () => totalBranches > 0 && branches.every((branch) => branch.checked),
    [branches, totalBranches]
  )

  const toggleAllBranches = () => {
    const nextChecked = !allSelected
    const nextBranches = branches.map((branch) => ({
      ...branch,
      checked: nextChecked,
      accounts: branch.accounts.map((account) => ({...account, checked: nextChecked})),
    }))
    setBranchesAndNotify(nextBranches)
  }

  const toggleBranch = (branchId: string | number) => {
    const nextBranches = branches.map((branch) => {
      if (branch.id !== branchId) return branch
      const nextChecked = !branch.checked
      return {
        ...branch,
        checked: nextChecked,
        accounts: branch.accounts.map((account) => ({...account, checked: nextChecked})),
      }
    })
    setBranchesAndNotify(nextBranches)
  }

  const toggleBranchExpand = (branchId: string | number) => {
    const nextBranches = branches.map((branch) =>
      branch.id === branchId ? {...branch, expanded: !branch.expanded} : branch
    )
    if (onBranchesChange) {
      onBranchesChange(nextBranches)
      return
    }
    setInternalBranches(nextBranches)
  }

  const toggleAccount = (branchId: string | number, accountId: string | number) => {
    const nextBranches = branches.map((branch) => {
      if (branch.id !== branchId) return branch

      const nextAccounts = branch.accounts.map((account) =>
        account.id === accountId ? {...account, checked: !account.checked} : account
      )

      return {
        ...branch,
        checked: nextAccounts.length > 0 && nextAccounts.every((account) => account.checked),
        accounts: nextAccounts,
      }
    })
    setBranchesAndNotify(nextBranches)
  }

  return (
    <div className={clsx('branches-menu', className)}>
      {showTitle && (
        <h3 className='branches-menu__title'>{intl.formatMessage({id: 'ACCOUNTS'})}</h3>
      )}

      <div className='branches-menu__search'>
        <div className='branches-menu__search-field'>
          <KTSVG path='/media/gori/magnify.svg' className='svg-icon svg-icon-2 flex-shrink-0' />
          <InputTextNoFormik
            type='text'
            className='branches-menu__search-input'
            inputClassName='border-0 p-0'
            placeholder={intl.formatMessage({id: 'SEARCH_ACCOUNTS'})}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <div
        className={clsx('branches-menu__row branches-menu__row--all', {
          'is-active': allSelected,
        })}
      >
        <div className='branches-menu__item'>
          <InputCheckNoFormik
            checked={allSelected}
            onChange={toggleAllBranches}
            label={`${intl.formatMessage({id: 'ALL_BRANCHES'})} (${totalBranches})`}
            labelClassName='branches-menu__text'
          />
        </div>
      </div>

      <div className='branches-menu__list'>
        {filteredBranches.map((branch) => (
          <div key={branch.id}>
            <div
              className={clsx('branches-menu__row', {
                'is-active': branch.checked && !allSelected,
              })}
            >
              <div className='branches-menu__item'>
                <InputCheckNoFormik
                  type='checkbox'
                  checked={!!branch.checked}
                  onChange={() => toggleBranch(branch.id)}
                  label={branch.name}
                  labelClassName='branches-menu__text'
                />
              </div>

              <button
                type='button'
                className='branches-menu__toggle'
                onClick={() => toggleBranchExpand(branch.id)}
                aria-expanded={branch.expanded}
              >
                <KTSVG
                  path={
                    branch.expanded ? '/media/gori/orders/minus.svg' : '/media/gori/orders/plus.svg'
                  }
                  className='svg-icon-2 branches-menu__toggle-icon'
                />
              </button>
            </div>

            {branch.expanded && (
              <div className='branches-menu__accounts'>
                {branch.accounts.map((account) => (
                  <div key={account.id} className='branches-menu__item branches-menu__item--child'>
                    <InputCheckNoFormik
                      type='checkbox'
                      checked={!!account.checked}
                      onChange={() => toggleAccount(branch.id, account.id)}
                      label={account.name}
                      labelClassName='branches-menu__text'
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export {BranchesMenu}
